import { SIGNOUT_ERROR } from '../constants/errorConstants';
import { setLocalAppMsg } from '../hooks/useAppMessages/useAppMessages';
import { HttpError } from '../types/common';
import { removeQuotesFromText } from './textHelpers';
import { hasUserFacingMessage } from './userFacingError';

/**
 * Normalises whatever a transport `catch` receives into an `HttpError`
 * without changing `message`. An `HttpError` passes through untouched; a
 * user-facing marker (`userMessage`, e.g. a wrapped Cognito error) is kept;
 * anything else (network failures, JSON parse errors, primitives) gets no
 * `userMessage`, so `getUserFacingErrorMessage` hides it from the user.
 */
export const toHttpError = (error: unknown, statusCode?: number): HttpError => {
  if (error instanceof HttpError) {
    return error;
  }

  const rawMessage =
    error instanceof Error
      ? error.message
      : (error as { message?: unknown } | null)?.message ?? error;
  const userMessage = hasUserFacingMessage(error)
    ? error.userMessage
    : undefined;

  return new HttpError(String(rawMessage), statusCode, { userMessage });
};

// Types for the result object with discriminated union
type Success<T> = {
  data: T;
  error: null;
};

type Failure<E> = {
  data: null;
  error: E;
};

interface Options {
  global: boolean;
}

type AuthServiceSignout = (parameters: Options) => Promise<any>;

export const signOutHelper = async (
  authServiceSignout: AuthServiceSignout,
  loginRoute: string,
  appMsg?: any,
) => {
  try {
    sessionStorage.clear();
    await authServiceSignout({ global: true });

    if (appMsg) {
      setLocalAppMsg(appMsg);
    }

    return window.location.replace(loginRoute);
  } catch (error) {
    const msg = (error as Error | TypeError).message;
    console.error({ error: msg });

    throw new Error(SIGNOUT_ERROR);
  }
};

type Result<T, E = Error> = Success<T> | Failure<E>;

// Main wrapper function
export async function tryCatch<T, E = Error>(
  input: Promise<T> | (() => Promise<T>),
): Promise<Result<T, E>> {
  try {
    const data = await (typeof input === 'function' ? input() : input);
    return { data, error: null };
  } catch (error) {
    // Mutate the message in place instead of spreading into an object literal.
    // Spreading drops the prototype chain, so `instanceof HttpError` would fail
    // downstream and parseError would lose the real statusCode (e.g. 503 → 500).
    if (error instanceof Error) {
      error.message = removeQuotesFromText(error.message) ?? error.message;
      return { data: null, error: error as E };
    }
    // Object-shaped throw that is NOT an Error (Amplify / backend / axios error
    // shapes carry `.message` plus fields callers read). Preserve the object —
    // only de-quote a string message. Wrapping it in a new Error would stringify
    // it to "[object Object]" and drop the real message.
    if (error !== null && typeof error === 'object') {
      const objectError = error as { message?: unknown };

      if (typeof objectError.message === 'string') {
        objectError.message =
          removeQuotesFromText(objectError.message) ?? objectError.message;
      }

      return { data: null, error: error as E };
    }
    // Primitive throw (string / number / boolean / null / undefined): wrap it so
    // callers always get an Error and we never assign `.message` on a primitive
    // (which throws in strict mode).
    return { data: null, error: new Error(String(error)) as E };
  }
}
